import { LEARNING_GOAL_OPTIONS, ROLE_OPTIONS_TYPE, SKILL_OPTIONS_TYPE } from 'bundles/onboarding-common/constants';
import type { LearnerObjectAttributesCurrentOccupationQuery } from 'bundles/onboarding-common/queries/__generated__/LearnerObjectAttributesCurrentOccupation';
import type { LearnerObjectAttributesLearningGoalQuery } from 'bundles/onboarding-common/queries/__generated__/LearnerObjectAttributesLearningGoal';
import type { LearnerProfileDesiredSkillsQuery } from 'bundles/onboarding-common/queries/__generated__/LearnerProfileDesiredSkills';
import type { LearnerProfileInterestedDomainsQueryQuery } from 'bundles/onboarding-common/queries/__generated__/LearnerProfileInterestedDomains';
import type { LearnerProfileLearningGoalQueryQuery } from 'bundles/onboarding-common/queries/__generated__/LearnerProfileLearningGoal';
import type { LearnerProfileOccupationPreferencesQueryQuery } from 'bundles/onboarding-common/queries/__generated__/LearnerProfileOccupationPreferences';
import type { Skill } from 'bundles/onboarding-common/types';
import { slugifyCustomRoleName } from 'bundles/onboarding-common/utils';

const formatLearningGoalFromLearnerProfile = (learningGoalData?: LearnerProfileLearningGoalQueryQuery) => {
  switch (learningGoalData?.LearnerProfile?.me?.learningPrograms?.learningGoal) {
    case 'CHANGE_CAREER':
      return LEARNING_GOAL_OPTIONS.LEARNING_GOAL_CHANGE_CAREER;
    case 'IMPROVE_SKILLS':
      return LEARNING_GOAL_OPTIONS.LEARNING_GOAL_IMPROVE_SKILLS;
    case 'LAND_NEW_JOB':
      return LEARNING_GOAL_OPTIONS.LEARNING_GOAL_LAND_NEW_JOB;
    case 'PERSONAL_INTEREST':
      return LEARNING_GOAL_OPTIONS.LEARNING_GOAL_PERSONAL_INTEREST;
    default:
      return null;
  }
};

const formatOccupationPreferencesFromLearnerProfile = (
  occupationPreferencesData?: LearnerProfileOccupationPreferencesQueryQuery
) => {
  const value =
    occupationPreferencesData?.LearnerProfile?.me?.occupationPreferences?.map((occupationPreference) => {
      if (occupationPreference.occupation.__typename === 'LearnerProfile_OccupationId') {
        return {
          id: occupationPreference.occupation.id,
          name: '',
          type: ROLE_OPTIONS_TYPE.DEFAULT,
        };
      } else {
        return {
          name: occupationPreference.occupation.name,
          id: slugifyCustomRoleName(occupationPreference.occupation.name),
          type: ROLE_OPTIONS_TYPE.CUSTOM,
        };
      }
    }) || null;

  return value;
};

const formatSkillsWantedFromLearnerProfile = (skillsWantedData?: LearnerProfileDesiredSkillsQuery) => {
  const value =
    skillsWantedData?.LearnerProfile?.me?.desiredSkills?.reduce<Skill[]>((acc, skill) => {
      if (skill.__typename === 'Skill_Skill') {
        return [
          ...acc,
          {
            id: skill.id,
            skillName: skill.skillName,
            type: SKILL_OPTIONS_TYPE.DEFAULT,
          },
        ];
      } else {
        return acc;
      }
    }, []) || null;

  return value;
};

const formatInterestedDomainsFromLearnerProfile = (
  interestedDomainsData?: LearnerProfileInterestedDomainsQueryQuery
) => {
  const value =
    interestedDomainsData?.LearnerProfile?.me?.interestedDomains?.reduce<string[]>((acc, domain) => {
      if (domain.__typename === 'Domain_Domain') {
        return [...acc, domain.id];
      } else if (domain.__typename === 'LearnerProfile_OtherDomain' && domain.value === 'OTHER') {
        return [...acc, 'other'];
      }
      return acc;
    }, []) || null;

  return value;
};

const formatLearningGoalFromLearnerObject = (learningGoalData?: LearnerObjectAttributesLearningGoalQuery) => {
  switch (learningGoalData?.LearnerObject?.attributesByUserId?.learningGoal) {
    case 'CHANGE_CAREER':
      return LEARNING_GOAL_OPTIONS.LEARNING_GOAL_CHANGE_CAREER;
    case 'IMPROVE_SKILLS':
      return LEARNING_GOAL_OPTIONS.LEARNING_GOAL_IMPROVE_SKILLS;
    case 'LAND_NEW_JOB':
      return LEARNING_GOAL_OPTIONS.LEARNING_GOAL_LAND_NEW_JOB;
    case 'PERSONAL_INTEREST':
      return LEARNING_GOAL_OPTIONS.LEARNING_GOAL_PERSONAL_INTEREST;
    default:
      return null;
  }
};

const formatCurrentOccupationFromLearnerObject = (
  currentOccupationData?: LearnerObjectAttributesCurrentOccupationQuery
): Record<string, string> | null => {
  const currentOccupation = currentOccupationData?.LearnerObject?.attributesByUserId?.currentOccupation;

  if (!currentOccupation || !currentOccupation.occupation) {
    return null;
  }

  if (currentOccupation.occupationInputType === 'DEFINED') {
    return { id: currentOccupation.occupation };
  } else if (currentOccupation.occupationInputType === 'USER_GENERATED') {
    return { name: currentOccupation.occupation };
  }

  return null;
};

export {
  formatLearningGoalFromLearnerProfile,
  formatLearningGoalFromLearnerObject,
  formatSkillsWantedFromLearnerProfile,
  formatOccupationPreferencesFromLearnerProfile,
  formatInterestedDomainsFromLearnerProfile,
  formatCurrentOccupationFromLearnerObject,
};
